/**
 * Unified Wikipedia Service - Step 9 Implementation
 * Combines WikipediaAPI and WikipediaService with Feature Bible compliance
 */

import { wikipediaFaithfulAPI, WikipediaFaithfulArticle } from '../wikipedia/wikipediaFaithfulAPI';
import { wikipediaService, WikipediaPage } from '../api/wikipediaService';
import { 
  SwipeUIFetchService, 
  SwipeArticle, 
  ArticleLink, 
  ServiceOptions, 
  ArticleId, 
  WikipediaServiceError,
  IdResolutionResult 
} from './types';
import { IdResolver } from './IdResolver';
import { UnifiedCacheManager } from './cache/UnifiedCacheManager';
import { inputValidator } from '@utils/validation/inputValidator';
import { threatMonitor } from '@services/security/threatMonitor';

/**
 * Service configuration options
 */
export interface UnifiedServiceOptions {
  cacheSize?: number;
  cacheTTL?: number;
  lookupCacheSize?: number;
  enableMetrics?: boolean;
  timeoutMs?: number;
  userAgent?: string;
}

/**
 * Unified Wikipedia service implementing Feature Bible requirements
 * Provides intelligent routing between content and metadata services
 */
export class UnifiedWikipediaService implements SwipeUIFetchService {
  private cacheManager: UnifiedCacheManager;
  private pendingRequests = new Map<string, Promise<SwipeArticle>>();
  private config: Required<UnifiedServiceOptions>;

  constructor(
    options: UnifiedServiceOptions = {},
    private contentService = wikipediaFaithfulAPI,
    private metadataService = wikipediaService
  ) {
    this.config = {
      cacheSize: options.cacheSize || 1000,
      cacheTTL: options.cacheTTL || 300000, // 5 minutes
      lookupCacheSize: options.lookupCacheSize || 2000,
      enableMetrics: options.enableMetrics !== false,
      timeoutMs: options.timeoutMs || 10000,
      userAgent: options.userAgent || 'BlueLinks-SwipeUI/1.0',
    };

    this.cacheManager = new UnifiedCacheManager({
      maxSize: this.config.cacheSize,
      defaultTTL: this.config.cacheTTL,
      lookupCacheSize: this.config.lookupCacheSize,
      enableMetrics: this.config.enableMetrics,
    });
  }

  /**
   * Get article by ID - Feature Bible core requirement
   */
  async getArticle(id: ArticleId, options: ServiceOptions = {}): Promise<SwipeArticle> {
    // Validate input
    if (!id) {
      throw new WikipediaServiceError('Article ID is required', 'INVALID_ID');
    }

    const startTime = Date.now();
    let parsed;
    
    try {
      parsed = IdResolver.parse(id);
    } catch (error) {
      await this.logRequest('get_article', id, 'error', options, error);
      throw error;
    }

    // Check cache first
    if (options.useCache !== false) {
      const cached = this.cacheManager.get(id);
      if (cached) {
        await this.logRequest('get_article', id, 'cache_hit', options, null, Date.now() - startTime);
        return this.applyCanBackFlag(cached, options);
      }
    }

    // Deduplicate concurrent requests
    const requestKey = `${IdResolver.hash(id)}:${JSON.stringify(options)}`;
    if (this.pendingRequests.has(requestKey)) {
      return this.pendingRequests.get(requestKey)!;
    }

    // Create request promise
    const requestPromise = this.fetchArticleData(id, parsed, options);
    this.pendingRequests.set(requestKey, requestPromise);

    try {
      const result = await requestPromise;
      
      // Cache successful result
      if (options.useCache !== false) {
        this.cacheManager.set(id, result, {
          ttl: options.timeout,
          category: this.determineCacheCategory(options),
        });
      }

      await this.logRequest('get_article', id, 'success', options, null, Date.now() - startTime);
      return this.applyCanBackFlag(result, options);

    } catch (error) {
      await this.logRequest('get_article', id, 'error', options, error, Date.now() - startTime);
      throw error;
    } finally {
      this.pendingRequests.delete(requestKey);
    }
  }

  /**
   * Fetch article data with intelligent service routing
   */
  private async fetchArticleData(
    id: ArticleId,
    parsed: ReturnType<typeof IdResolver.parse>,
    options: ServiceOptions
  ): Promise<SwipeArticle> {
    const needsContent = options.includeContent !== false;
    const needsMetadata = options.includeMetadata !== false || options.includeLinks !== false;

    // Handle temporary cards
    if (parsed.type === 'temp') {
      return this.createTempArticle(id, parsed.value);
    }

    let title: string;
    let pageId: number | undefined;

    // Resolve title and page ID
    if (parsed.type === 'page') {
      pageId = parseInt(parsed.value, 10);
      title = await this.resolveTitle(pageId) || `Page ${pageId}`;
    } else {
      title = IdResolver.normalizeTitle(parsed.value);
      pageId = await this.resolvePageId(title) || undefined;
    }

    // Fetch data based on requirements
    const fetchPromises: Promise<any>[] = [];
    
    if (needsContent) {
      fetchPromises.push(
        this.fetchContent(title).catch(err => {
          console.warn(`Content fetch failed for "${title}":`, err);
          return null;
        })
      );
    } else {
      fetchPromises.push(Promise.resolve(null));
    }

    if (needsMetadata) {
      fetchPromises.push(
        this.fetchMetadata(title, options).catch(err => {
          console.warn(`Metadata fetch failed for "${title}":`, err);
          return null;
        })
      );
    } else {
      fetchPromises.push(Promise.resolve(null));
    }

    const [contentData, metadataData] = await Promise.all(fetchPromises);

    // Build unified article
    return this.buildSwipeArticle(id, title, pageId, contentData, metadataData, options);
  }

  /**
   * Fetch content from content service
   */
  private async fetchContent(title: string): Promise<WikipediaFaithfulArticle | null> {
    try {
      // Detect mobile layout (width <= 768px)
      const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
      return await this.contentService.getArticle(title, { mobileLayout: isMobile });
    } catch (error) {
      // Content service failures are not fatal
      console.warn(`Content service failed for "${title}":`, error);
      return null;
    }
  }

  /**
   * Fetch metadata from metadata service
   */
  private async fetchMetadata(title: string, options: ServiceOptions): Promise<WikipediaPage | null> {
    try {
      return await this.metadataService.getPage(title, {
        useCache: options.useCache,
        timeout: options.timeout,
        priority: options.priority,
        source: options.source || 'swipe-ui',
      });
    } catch (error) {
      // Metadata service failures are not fatal
      console.warn(`Metadata service failed for "${title}":`, error);
      return null;
    }
  }

  /**
   * Build unified SwipeArticle from service responses
   */
  private buildSwipeArticle(
    originalId: ArticleId,
    title: string,
    pageId: number | undefined,
    contentData: WikipediaFaithfulArticle | null,
    metadataData: WikipediaPage | null,
    options: ServiceOptions
  ): SwipeArticle {
    // Determine final title (prefer metadata service)
    const finalTitle = metadataData?.title || contentData?.title || title;
    
    // Create proper article ID
    let articleId: ArticleId;
    if (pageId) {
      articleId = IdResolver.createPageId(pageId);
    } else {
      articleId = IdResolver.createTitleId(finalTitle);
    }

    // Build article links if requested
    let links: ArticleLink[] | undefined;
    if (options.includeLinks !== false && metadataData?.links) {
      links = metadataData.links.map(link => ({
        title: link.title,
        displayText: link.displayTitle || link.title,
        exists: link.exists,
        namespace: link.namespace,
        // Create proper article ID for the link
        id: link.exists ? IdResolver.createTitleId(link.title) : undefined,
      }));
    }

    return {
      // Feature Bible required fields
      id: articleId,
      title: finalTitle,
      url: IdResolver.toUrlPath(articleId, finalTitle),
      canBack: false, // Will be set by context layer

      // Enhanced content fields
      content: contentData?.content,
      extract: metadataData?.extract,
      links,

      // Wikipedia faithful rendering CSS
      cssUrl: contentData?.cssUrl,
      baseStyles: contentData?.baseStyles,

      // Metadata
      metadata: metadataData ? {
        pageId: metadataData.id,
        lastModified: metadataData.lastModified,
        categories: metadataData.categories,
        thumbnail: metadataData.thumbnail,
        coordinates: metadataData.coordinates,
        pageviews: metadataData.pageviews,
        langlinks: metadataData.langlinks,
      } : pageId ? {
        pageId,
      } : undefined,
    };
  }

  /**
   * Create temporary article for temp: IDs
   */
  private createTempArticle(id: ArticleId, tempValue: string): SwipeArticle {
    return {
      id,
      title: `Temporary: ${tempValue}`,
      url: `/temp/${encodeURIComponent(tempValue)}`,
      canBack: false,
      content: '<p>This is a temporary article placeholder.</p>',
      extract: 'Temporary article placeholder',
      metadata: {
        pageId: 0,
        lastModified: new Date().toISOString(),
        categories: ['Temporary'],
      },
    };
  }

  /**
   * Apply canBack flag based on options or context
   */
  private applyCanBackFlag(article: SwipeArticle, options: ServiceOptions): SwipeArticle {
    // Context layer will set this properly, but we can provide defaults
    return {
      ...article,
      canBack: false, // Always false from service - context sets this
    };
  }

  /**
   * Get article links
   */
  async getArticleLinks(id: ArticleId, limit: number = 50): Promise<ArticleLink[]> {
    const article = await this.getArticle(id, {
      includeContent: false,
      includeLinks: true,
      includeMetadata: false,
    });

    const links = article.links || [];
    return limit > 0 ? links.slice(0, limit) : links;
  }

  /**
   * Resolve title to page ID with caching
   */
  async resolvePageId(title: string): Promise<number | null> {
    const normalizedTitle = IdResolver.normalizeTitle(title);
    
    // Check cache first
    const cachedPageId = this.cacheManager.lookupCache?.getPageIdByTitle?.(normalizedTitle);
    if (cachedPageId) {
      return cachedPageId;
    }

    try {
      // Use metadata service to resolve
      const pageData = await this.metadataService.getPage(normalizedTitle, {
        useCache: true,
        priority: 'low',
        source: 'id-resolution',
      });

      if (pageData?.id) {
        // Update cache
        this.cacheManager.updateLookupCache({
          id: IdResolver.createPageId(pageData.id),
          title: pageData.title,
          url: '',
          canBack: false,
          metadata: { pageId: pageData.id },
        });
        
        return pageData.id;
      }
    } catch (error) {
      console.warn(`Failed to resolve page ID for title "${normalizedTitle}":`, error);
    }

    return null;
  }

  /**
   * Resolve page ID to current title
   */
  async resolveTitle(pageId: number): Promise<string | null> {
    // Check cache first
    const cachedTitle = this.cacheManager.lookupCache?.getTitleByPageId?.(pageId);
    if (cachedTitle) {
      return cachedTitle;
    }

    // Page ID to title resolution is more complex and would require
    // additional Wikipedia API calls. For now, return null and rely
    // on the calling code to handle missing titles.
    console.warn(`Title resolution for page ID ${pageId} not implemented`);
    return null;
  }

  /**
   * Batch resolve multiple identifiers
   */
  async batchResolveIds(identifiers: string[]): Promise<Map<string, ArticleId>> {
    const results = new Map<string, ArticleId>();
    
    // Process in parallel with reasonable concurrency limit
    const batchSize = 10;
    for (let i = 0; i < identifiers.length; i += batchSize) {
      const batch = identifiers.slice(i, i + batchSize);
      const batchPromises = batch.map(async (identifier) => {
        try {
          const articleId = IdResolver.fromLegacyId(identifier);
          return { identifier, articleId };
        } catch (error) {
          console.warn(`Failed to resolve identifier "${identifier}":`, error);
          return { identifier, articleId: null };
        }
      });

      const batchResults = await Promise.all(batchPromises);
      for (const { identifier, articleId } of batchResults) {
        if (articleId) {
          results.set(identifier, articleId);
        }
      }
    }

    return results;
  }

  /**
   * Clear all caches
   */
  clearCache(): void {
    this.cacheManager.clear();
    this.contentService.clearCache();
    this.metadataService.clearCache();
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    return this.cacheManager.getStats();
  }

  /**
   * Get service health status
   */
  getHealthStatus() {
    return this.cacheManager.getHealthStatus();
  }

  /**
   * Determine cache category based on options
   */
  private determineCacheCategory(options: ServiceOptions): 'full' | 'metadata-only' | 'content-only' {
    const hasContent = options.includeContent !== false;
    const hasMetadata = options.includeMetadata !== false || options.includeLinks !== false;

    if (hasContent && hasMetadata) return 'full';
    if (hasContent) return 'content-only';
    if (hasMetadata) return 'metadata-only';
    return 'full'; // Default
  }

  /**
   * Log service requests for monitoring
   */
  private async logRequest(
    operation: string,
    target: string,
    status: 'success' | 'error' | 'cache_hit',
    options?: ServiceOptions,
    error?: any,
    durationMs?: number
  ): Promise<void> {
    const severity = status === 'error' ? 'medium' : 'low';

    threatMonitor.logEvent({
      type: 'system_error' as const,
      severity,
      source: `SwipeUI Wikipedia ${operation}: ${status}`,
      details: {
        operation,
        target: IdResolver.describe(target as ArticleId),
        status,
        durationMs,
        options: options ? {
          includeContent: options.includeContent,
          includeLinks: options.includeLinks,
          includeMetadata: options.includeMetadata,
          priority: options.priority,
          source: options.source,
        } : undefined,
        error: error?.message,
        errorCode: error?.code,
      },
    });
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    this.cacheManager.destroy();
    this.pendingRequests.clear();
  }
}